import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GetParticipantDataQuery as GetParticipantData } from '__generated__/graphql';

import { emailRegex, getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import EditParticipantBackendMutation from './EditParticipantBackendMutation';
import EmailInput from '../../../../common/ParticipantAttributes/Attributes/EmailInput';
import GetParticipantDataQuery from '../GetParticipantDataQuery';
import GetRegistrationSummaryQuery from './GetRegistrationSummaryQuery';
import NameInput from '../../../../common/ParticipantAttributes/Attributes/NameInput';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useParticipantExistenceCheck from '../../../../common/useParticipantExistenceCheck';
import useProject from '../../../useProject';
import useScroll from '../../../../common/useScroll';
import useValidation from '../../../../common/useValidation';

type Participant = GetParticipantData['project']['participant'];

export interface EditParticipantFormProps {
  participant: Participant;
  onClose?: () => void;
  onSuccess?: () => void;
  refetchQueries?: DocumentNode[];
}

const EditParticipantForm = ({
  participant,
  onSuccess,
  onClose,
  refetchQueries = [GetParticipantDataQuery, GetRegistrationSummaryQuery],
}: EditParticipantFormProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const project = useProject();
  const notifier = useNotifier();
  const { scrollTo } = useScroll();

  const form = useForm({
    values: {
      first_name: participant.first_name,
      last_name: participant.last_name,
      email: participant.email,
    },
  });

  const [
    editParticipant,
    { loading, error, client },
  ] = useMutation(
    EditParticipantBackendMutation,
    {
      variables: {
        input: {
          id: participant.id,
          ...form.values,
        },
      },
      onCompleted: async ({ editParticipant }) => {
        // Normally, refetch the queries after editing the participant.
        // When the participant has been merged with another participant, redirect to the new participant page.
        if (!editParticipant || editParticipant.id === participant.id) {
          await client.refetchQueries({
            include: refetchQueries,
          });
        } else {
          history.push(`/${project.id}/participants/${editParticipant.id}`);
        }

        onSuccess?.();
        notifier.success(t('notifications.contact_saved'));
      },
      onError: () => {
        scrollTo('EditParticipantForm', { scrollContainer: 'Modal' });
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: editParticipantSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const { participantExists } = useParticipantExistenceCheck({
    participant: form.values,
    enabled: participant.email.toLowerCase() !== form.values.email.toLowerCase(),
  });

  return (
    <UI.Form id="EditParticipantForm" onSubmit={() => editParticipant()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <NameInput
          value={form.values}
          onChange={(value) => form.set((participant) => ({ ...participant, ...value }))}
          onBlur={(key) => form.setTouched(key, true)}
          touched={form.touched}
          required
          errors={errors}
        />

        <EmailInput
          value={form.values}
          onChange={(value) => form.set((participant) => ({ ...participant, ...value }))}
          onBlur={(key) => form.setTouched(key, true)}
          touched={form.touched}
          errors={errors}
          required
        />

        {participantExists && (
          <UI.Warning>
            {t('edit_contact_exists_warning')}
          </UI.Warning>
        )}

        <UI.FormButtons
          form={form}
          submitLabel={t('save_contact')}
          onClose={onClose}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

const editParticipantSchema = yup.object({
  email: yup.string().required().matches(emailRegex, { name: 'email' }),
  first_name: yup.string().required(),
  last_name: yup.string().required(),
});

export default EditParticipantForm;
